/**
 * CreateAgentFlow — the simple way (approved mockup
 * .superpowers/brainstorm/76185-1792210064/content/create-agent-simple-v1.html):
 *
 *   ① one line in  →  ② Kiaan fills one card  →  change by words or tap a line  →  ③ "Looks good — create"  →  ④ ready + Test now
 *
 * Every old option is still there: "More settings" (and tapping a card line) opens
 * CreateAgentAdvanced, filled from the card. Safe defaults come from the server
 * (agent-draft.ts clampDraft): asks before sending, never touches money, never reads cost prices or salaries.
 */
import { useMemo, useState } from 'react';
import {
  createAgent,
  draftAgent,
  kiaanErrorText,
  testAgent,
  type AgentKindName,
  type KiaanAgent,
  type KiaanAgentDraft,
  type KiaanAgentInput,
} from './kiaanApi';
import { CreateAgentAdvanced } from './CreateAgentAdvanced';

export interface CreateAgentFlowProps {
  agents: KiaanAgent[];
  onClose: () => void;
  /** `open` = the user pressed "Open agent" on the ready screen. */
  onCreated: (agent: KiaanAgent, open?: boolean) => void;
}

export const STARTERS: Array<{ icon: string; label: string; line: string; kind?: AgentKindName }> = [
  { icon: '💰', label: 'Chase late payments', line: 'Chase customers who have not paid' },
  { icon: '📊', label: 'Daily sales summary', line: 'Write a daily sales summary' },
  { icon: '💬', label: 'Answer dealer questions', line: 'Answer dealer questions about products and prices' },
  { icon: '🧾', label: 'Check new quotes', line: 'Check new quotes for mistakes' },
  { icon: '🛠', label: 'Fix a bug (developer)', line: 'A developer that fixes bugs', kind: 'Developer' },
];

const PACK_LABEL: Record<string, string> = {
  invoices_payments: 'Invoices',
  customer_list: 'Customer list',
  product_prices: 'Product prices',
  company_rules: 'Company rules',
  cost_prices: 'Cost prices',
  salaries_hr: 'Salaries',
};
/** Shown red ("no") on every card so the user sees they are off. */
const ALWAYS_SHOWN_OFF = ['cost_prices', 'salaries_hr'];

/** Card line → the step it opens in More settings. */
export const LINE_STEP: Record<string, string> = {
  top: 'Job',
  team: 'Team & boss',
  reads: 'Knowledge',
  does: 'Tools & folders',
  never: 'Rules & test',
  runs: 'Rules & test',
  brain: 'Brain (AI model)',
  code: 'Code & git',
};

export function draftToInput(d: KiaanAgentDraft): KiaanAgentInput & { kind: AgentKindName } {
  return {
    kind: d.kind,
    name: d.name,
    job: d.job,
    teamName: d.teamName,
    bossAgentId: null,
    brain: d.brain,
    monthlyLimit: d.monthlyLimit,
    knowledgePacks: d.knowledgePacks,
    ...(d.kind === 'Developer' ? { codeTree: d.codeTree } : {}),
    tools: d.tools,
    rules: d.rules,
  };
}

function doesText(d: KiaanAgentDraft): string {
  const wa = d.tools.find((t) => t.name === 'ChatKart WhatsApp')?.access ?? 'off';
  const mail = d.tools.find((t) => t.name === 'MailKart email')?.access ?? 'off';
  const parts: string[] = [];
  if (wa !== 'off') parts.push('write WhatsApp messages');
  if (mail !== 'off') parts.push('write emails');
  const folders = d.tools.find((t) => t.name === 'Folders')?.access ?? 'off';
  if (folders === 'read + write') parts.push('change files');
  else if (folders === 'read') parts.push('read files');
  if (parts.length === 0) return 'Read and report only';
  const s = parts.join(', ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

type Bubble = { who: 'me' | 'kiaan'; text: string };
type Phase = 'ask' | 'card' | 'done' | 'more';

export function CreateAgentFlow({ agents, onClose, onCreated }: CreateAgentFlowProps) {
  const teams = useMemo(() => Array.from(new Set(agents.map((a) => a.teamName).filter(Boolean))), [agents]);
  const [phase, setPhase] = useState<Phase>('ask');
  const [line, setLine] = useState('');
  const [kind, setKind] = useState<AgentKindName | undefined>(undefined);
  const [draft, setDraft] = useState<KiaanAgentDraft | null>(null);
  const [changed, setChanged] = useState<string[]>([]);
  const [chat, setChat] = useState<Bubble[]>([]);
  const [changing, setChanging] = useState(false);
  const [changeText, setChangeText] = useState('');
  const [busy, setBusy] = useState<'draft' | 'change' | 'save' | 'test' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<KiaanAgent | null>(null);
  const [testNote, setTestNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [moreStep, setMoreStep] = useState<string | undefined>(undefined);

  const start = async (text: string, k?: AgentKindName) => {
    const l = text.trim();
    if (!l || busy) return;
    setBusy('draft');
    setError(null);
    try {
      const r = await draftAgent({ line: l, kind: k, teams });
      setDraft(r.draft);
      setChanged([]);
      setChat([
        { who: 'me', text: l },
        { who: 'kiaan', text: r.message || 'Here is the agent I made. Check it. Tap any line to change it.' },
      ]);
      setPhase('card');
    } catch (err) {
      setError(kiaanErrorText(err));
    } finally {
      setBusy(null);
    }
  };

  const change = async () => {
    const c = changeText.trim();
    if (!c || !draft || busy) return;
    setBusy('change');
    setError(null);
    try {
      const r = await draftAgent({ change: c, current: draft });
      setDraft(r.draft);
      setChanged(r.changed);
      setChat((b) => [
        ...b,
        { who: 'me', text: c },
        { who: 'kiaan', text: r.message || (r.changed.length ? `${r.changed.length === 1 ? '1 line' : `${r.changed.length} lines`} changed.` : 'Nothing needed to change.') },
      ]);
      setChangeText('');
      setChanging(false);
    } catch (err) {
      setError(kiaanErrorText(err));
    } finally {
      setBusy(null);
    }
  };

  const needsCode = draft?.kind === 'Developer' && draft.codeTree.length === 0;

  const create = async () => {
    if (!draft || busy || needsCode) return;
    setBusy('save');
    setError(null);
    try {
      const { kind: _k, ...input } = draftToInput(draft);
      const a = await createAgent(input);
      setCreated(a);
      setPhase('done');
    } catch (err) {
      setError(kiaanErrorText(err));
    } finally {
      setBusy(null);
    }
  };

  const runTest = async () => {
    if (!draft || busy) return;
    setBusy('test');
    setTestNote(null);
    try {
      const { kind: _k, ...input } = draftToInput(draft);
      const r = await testAgent(input);
      setTestNote({ ok: r.ok, text: r.message || (r.ok ? 'Test passed.' : 'Test did not pass.') });
    } catch (err) {
      setTestNote({ ok: false, text: kiaanErrorText(err) });
    } finally {
      setBusy(null);
    }
  };

  const openMore = (lineKey?: string) => {
    setMoreStep(lineKey ? LINE_STEP[lineKey] : undefined);
    setPhase('more');
  };

  if (phase === 'more') {
    return (
      <CreateAgentAdvanced
        agents={agents}
        onClose={onClose}
        onCreated={(a) => onCreated(a)}
        initial={draft ? draftToInput(draft) : undefined}
        initialStep={moreStep}
        onBack={draft ? () => setPhase('card') : () => setPhase('ask')}
      />
    );
  }

  const head = (
    <div className="kw-head" style={{ background: 'transparent' }}>
      <span className="kw-small">· Agents · New agent</span>
      <span className="kw-sp" />
      <button type="button" className="kw-icon-btn" onClick={onClose} aria-label="Close new agent">✕</button>
    </div>
  );

  const errorBanner = error && (
    <div className="kw-banner kw-danger" role="alert">
      {error} <button type="button" className="kw-link" onClick={() => openMore()}>Fill it in by hand</button>
    </div>
  );

  if (phase === 'ask') {
    return (
      <div className="kw-body kca" data-testid="create-agent-flow">
        {head}
        <div className="kca-col">
          <div className="kca-bubble">What should this agent do? Say it in one line.</div>
          <form
            className="kca-ask"
            onSubmit={(e) => {
              e.preventDefault();
              void start(line, kind);
            }}
          >
            <input
              className="kw-fld"
              placeholder="e.g. Chase customers who have not paid"
              value={line}
              onChange={(e) => setLine(e.target.value)}
              aria-label="What should this agent do"
              autoFocus
            />
            <button type="submit" className="kw-btn" disabled={!line.trim() || busy !== null}>{busy === 'draft' ? 'Making…' : 'Go'}</button>
          </form>
          <div className="kw-small">Or start from one of these:</div>
          <div className="kca-chips">
            {STARTERS.map((s) => (
              <button
                key={s.label}
                type="button"
                className="kca-chip"
                disabled={busy !== null}
                onClick={() => {
                  setLine(s.line);
                  setKind(s.kind);
                  void start(s.line, s.kind);
                }}
              >
                {s.icon} {s.label}
              </button>
            ))}
          </div>
          {errorBanner}
          <button type="button" className="kca-more" onClick={() => openMore()}>More settings ▸ <span className="kw-small">(fill everything in by hand)</span></button>
        </div>
      </div>
    );
  }

  if (!draft) return null;

  if (phase === 'done' && created) {
    return (
      <div className="kw-body kca" data-testid="create-agent-done">
        {head}
        <div className="kca-col">
          <div className="kca-bubble">
            <b>{created.name || draft.name} is ready.</b> It is in the {draft.teamName}. It is also in Kiaan Work.
          </div>
          <div className="kca-card">
            <div className="kca-line"><span className="kca-l">First run</span><span className="kca-v">{draft.rules.schedule}</span></div>
            <div className="kca-line"><span className="kca-l">Test now</span><span className="kca-v">Checks the agent once. Sends nothing.</span></div>
          </div>
          {testNote && <div className={`kw-banner ${testNote.ok ? 'kw-ok' : 'kw-danger'}`} role="status">{testNote.text}</div>}
          <div className="kca-btns">
            <button type="button" className="kw-btn" onClick={() => void runTest()} disabled={busy !== null}>{busy === 'test' ? 'Testing…' : '▶ Test now'}</button>
            <button type="button" className="kw-btn kw-light" onClick={() => onCreated(created, true)}>Open agent</button>
            <button type="button" className="kw-btn kw-light" onClick={() => onCreated(created)}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  const L = (key: string, label: string, value: React.ReactNode, editable = true) => (
    <button
      type="button"
      className={`kca-line${changed.includes(key) ? ' kca-hl' : ''}`}
      data-line={key}
      onClick={editable ? () => openMore(key) : undefined}
      disabled={!editable}
      aria-label={`${label}: change`}
    >
      <span className="kca-l">{label}</span>
      <span className="kca-v">{value}</span>
      {editable && <span className="kca-e" aria-hidden="true">✎</span>}
    </button>
  );

  const isDev = draft.kind === 'Developer';
  return (
    <div className="kw-body kca" data-testid="create-agent-card">
      {head}
      <div className="kca-col">
        {chat.map((b, i) => (
          <div key={i} className={b.who === 'me' ? 'kca-me' : 'kca-bubble'}>{b.text}</div>
        ))}
        <div className="kca-card">
          <button type="button" className={`kca-top${changed.includes('top') ? ' kca-hl' : ''}`} onClick={() => openMore('top')}>
            <span className={`kca-av${isDev ? ' kca-dev' : ''}`} aria-hidden="true">{draft.icon}</span>
            <span>
              <b>{draft.name}</b>
              <span className="kca-sub">{draft.job}</span>
            </span>
          </button>
          {L('team', 'Team', <>{draft.teamName} · owner <b>you</b></>)}
          {isDev ? (
            <>
              {L('code', 'Code', draft.codeTree.length ? draft.codeTree.map((p) => <span key={p} className="kw-pill kw-wip">{p}</span>) : <span className="kw-pill kw-wt">pick which part</span>)}
              {L('works', 'Works on', 'Its own copy of the code (own branch)', false)}
              {L('finds', 'Finds code', 'Code graph only', false)}
              {L('merge', 'To test site', <>Merge into develop <span className="kw-pill kw-wt">asks you first</span></>, false)}
              {L('live', 'Go live', <><span className="kw-pill kw-bad">Ardhanex approves</span> — locked</>, false)}
            </>
          ) : (
            <>
              {L('reads', 'Can read', (
                <>
                  {draft.knowledgePacks.map((p) => <span key={p} className="kw-pill kw-ok">{PACK_LABEL[p] ?? p}</span>)}
                  {ALWAYS_SHOWN_OFF.filter((p) => !draft.knowledgePacks.includes(p)).map((p) => <span key={p} className="kw-pill kw-bad">{PACK_LABEL[p]}</span>)}
                </>
              ))}
              {L('does', 'Can do', <>{doesText(draft)} <span className="kw-pill kw-wt">{draft.rules.sendsMessage === 'ask first' ? 'asks you first' : 'drafts only'}</span></>)}
              {L('never', 'Never', draft.rules.changesMoney === 'never' ? 'Change money, delete anything' : 'Delete anything · asks before changing money')}
            </>
          )}
          {L('runs', 'Runs', draft.rules.schedule)}
          {L('brain', 'Brain', `${draft.brain === 'claude' ? 'Claude' : draft.brain === 'openrouter' ? 'Fast & cheap' : 'Kiaan chooses'} · up to ₹${draft.monthlyLimit.toLocaleString('en-IN')} / month`)}
        </div>

        {errorBanner}
        {needsCode && <div className="kw-small">Pick which part of the code it may change first. Tap the Code line.</div>}

        {changing ? (
          <form
            className="kca-ask"
            onSubmit={(e) => {
              e.preventDefault();
              void change();
            }}
          >
            <input
              className="kw-fld"
              placeholder="e.g. Run it Monday and Thursday only"
              value={changeText}
              onChange={(e) => setChangeText(e.target.value)}
              aria-label="What should change"
              autoFocus
            />
            <button type="submit" className="kw-btn" disabled={!changeText.trim() || busy !== null}>{busy === 'change' ? 'Changing…' : 'Change'}</button>
          </form>
        ) : (
          <div className="kca-btns">
            <button type="button" className="kw-btn" onClick={() => void create()} disabled={busy !== null || needsCode}>
              {busy === 'save' ? 'Creating…' : '✓ Looks good — create'}
            </button>
            <button type="button" className="kw-btn kw-light" onClick={() => setChanging(true)} disabled={busy !== null}>Change something</button>
          </div>
        )}
        <button type="button" className="kca-more" onClick={() => openMore()}>More settings ▸ <span className="kw-small">(all the old options: tools, levels, limits)</span></button>
      </div>
    </div>
  );
}
